/**
 * Data hooks for the Play Archive.
 *
 * The page stays a description of the screen; everything with a lifecycle
 * lives here: the URL query, the whole-event fetch, the versions fetch, the
 * viewport and container probes, and the per-user column preference.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ApiClient } from '../../lib/api-client';
import { ApiError } from '../../lib/api-error';
import type { PlayVersionPage, PlayView } from '../../lib/api-types';

import type { ArchiveColumn, FilterFacet } from './archive-columns';
import {
  ARCHIVE_COLUMNS_LAYOUT_NAME,
  ARCHIVE_COLUMNS_SCREEN_ID,
  fromLayout,
  readCachedPrefs,
  reconcilePrefs,
  toLayout,
  writeCachedPrefs,
  type ColumnPrefs,
} from './archive-layout';
import { readArchiveQuery, writeArchiveQuery, type ArchiveQuery } from './archive-query';

/** Card layout below 640px. */
const PHONE_MEDIA_QUERY = '(max-width: 639px)';

/** `GET /plays/v1` page size (the service maximum). */
export const PLAYS_PAGE_LIMIT = 200;
/** Hard stop so a runaway total can never loop forever. */
const MAX_PAGES = 10;

/**
 * A failure a coach can read. `ApiError.message` is already the server's RFC
 * 7807 `detail`; anything else gets a plain sentence rather than a stack trace.
 */
export function readableError(cause: unknown): { message: string; field: string | null } {
  if (ApiError.isApiError(cause)) return { message: cause.message, field: cause.field };
  return { message: 'Something went wrong. Please try again.', field: null };
}

export function useIsPhoneViewport(): boolean {
  const [isPhone, setIsPhone] = useState(
    () => globalThis.matchMedia?.(PHONE_MEDIA_QUERY).matches === true
  );
  useEffect(() => {
    const media = globalThis.matchMedia?.(PHONE_MEDIA_QUERY);
    if (media === undefined) return undefined;
    const onChange = (change: MediaQueryListEvent): void => setIsPhone(change.matches);
    setIsPhone(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);
  return isPhone;
}

/**
 * Content-box width and font size of an element, tracked with ResizeObserver.
 * `fallback` is used until (or where) layout is unavailable — jsdom reports 0.
 */
export function useElementBox<T extends HTMLElement>(
  fallback: number
): [(element: T | null) => void, { width: number; fontSize: number; fontFamily: string }] {
  const [element, setElement] = useState<T | null>(null);
  const [box, setBox] = useState({ width: fallback, fontSize: 0, fontFamily: '' });
  useLayoutEffect(() => {
    if (element === null) return undefined;
    const measure = (): void => {
      const width = element.clientWidth;
      const style = globalThis.getComputedStyle?.(element);
      const fontSize = Number.parseFloat(style?.fontSize ?? '') || 0;
      const fontFamily = style?.fontFamily ?? '';
      if (width > 0) {
        setBox((current) =>
          current.width === width &&
          current.fontSize === fontSize &&
          current.fontFamily === fontFamily
            ? current
            : { width, fontSize, fontFamily }
        );
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      globalThis.addEventListener?.('resize', measure);
      return () => globalThis.removeEventListener?.('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, box];
}

export interface ArchiveQueryState {
  query: ArchiveQuery;
  setQuery: (next: ArchiveQuery) => void;
  /** `?event_id=` wins over the session's active event, so links are shareable. */
  eventIdParam: string | null;
}

/** The archive's filter + sort state, held in the URL and nowhere else. */
export function useArchiveQuery(
  facets: ReadonlyMap<string, { facet: FilterFacet }>,
  columns: readonly ArchiveColumn[]
): ArchiveQueryState {
  const [searchParams, setSearchParams] = useSearchParams();
  const columnIds = useMemo(() => new Set(columns.map((column) => column.id)), [columns]);
  const query = useMemo(
    () => readArchiveQuery(searchParams, facets, columnIds),
    [searchParams, facets, columnIds]
  );
  const setQuery = useCallback(
    (next: ArchiveQuery): void => {
      setSearchParams((current) => writeArchiveQuery(next, current), { replace: true });
    },
    [setSearchParams]
  );
  return { query, setQuery, eventIdParam: searchParams.get('event_id') };
}

export interface ArchivePlaysState {
  plays: readonly PlayView[];
  /** Live patches (a badge appearing) edit the list in place, without a refetch. */
  patchPlays: (update: (current: readonly PlayView[]) => readonly PlayView[]) => void;
  isLoading: boolean;
  /** True once the first load for this event has landed. */
  hasLoaded: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Every play of the event: `GET /plays/v1?event_id=…&limit=200`, paged until
 * `total` is reached. Sort / filter happen client-side on the result.
 */
export function useArchivePlays(options: {
  apiClient: ApiClient;
  eventId: string | null;
}): ArchivePlaysState {
  const { apiClient, eventId } = options;
  const [plays, setPlays] = useState<readonly PlayView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const loadedEventRef = useRef<string | null>(null);

  useEffect(() => {
    if (eventId === null) {
      setPlays([]);
      setHasLoaded(false);
      return undefined;
    }
    if (loadedEventRef.current !== eventId) {
      loadedEventRef.current = eventId;
      setPlays([]);
      setHasLoaded(false);
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    (async () => {
      const all: PlayView[] = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const result = await apiClient.listPlays(
          {
            event_id: eventId,
            unit: 'all',
            sort: 'play_number',
            order: 'asc',
            page,
            limit: PLAYS_PAGE_LIMIT,
          },
          controller.signal
        );
        all.push(...result.items);
        if (result.items.length < PLAYS_PAGE_LIMIT || all.length >= result.total) break;
      }
      return all;
    })()
      .then((all) => {
        if (controller.signal.aborted) return;
        setPlays(all);
        setHasLoaded(true);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(readableError(cause).message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, eventId, refreshToken]);

  const patchPlays = useCallback(
    (update: (current: readonly PlayView[]) => readonly PlayView[]) =>
      setPlays((current) => update(current)),
    []
  );
  const reload = useCallback(() => setRefreshToken((token) => token + 1), []);

  return { plays, patchPlays, isLoading, hasLoaded, error, reload };
}

export interface ArchiveVersionsState {
  versions: PlayVersionPage | null;
  isLoading: boolean;
  error: string | null;
  load: (playId: string) => void;
  clear: () => void;
}

/** `GET /plays/v1/{play_id}/versions` for the expanded row. */
export function useArchiveVersions(apiClient: ApiClient): ArchiveVersionsState {
  const [versions, setVersions] = useState<PlayVersionPage | null>(null);
  const [state, setState] = useState<{ isLoading: boolean; error: string | null }>({
    isLoading: false,
    error: null,
  });

  const load = useCallback(
    (playId: string): void => {
      setState({ isLoading: true, error: null });
      apiClient
        .getPlayVersions(playId)
        .then((page) => {
          setVersions(page);
          setState({ isLoading: false, error: null });
        })
        .catch((cause: unknown) => {
          setVersions(null);
          setState({ isLoading: false, error: readableError(cause).message });
        });
    },
    [apiClient]
  );

  return {
    versions,
    isLoading: state.isLoading,
    error: state.error,
    load,
    clear: useCallback(() => setVersions(null), []),
  };
}

export interface ColumnPrefsState {
  prefs: ColumnPrefs;
  setPrefs: (next: ColumnPrefs) => void;
  /** Server save failed; the choice is kept on this device only. */
  isSavedLocallyOnly: boolean;
}

/**
 * Per-user column preference: localStorage paints instantly (offline cache),
 * `GET /users/v1/layouts` (screen `archive.columns`) wins when it lands, and
 * every change is written to both.
 */
export function useColumnPrefs(
  apiClient: ApiClient,
  columns: readonly ArchiveColumn[]
): ColumnPrefsState {
  const [stored, setStored] = useState(() => readCachedPrefs());
  const [isSavedLocallyOnly, setIsSavedLocallyOnly] = useState(false);
  const touchedRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    apiClient
      .listLayouts(controller.signal)
      .then((page) => {
        if (controller.signal.aborted || touchedRef.current) return;
        const record = page.items.find(
          (item) => item.scope === 'user' && item.screen_id === ARCHIVE_COLUMNS_SCREEN_ID
        );
        const parsed = record === undefined ? null : fromLayout(record.layout);
        if (parsed !== null) setStored(parsed);
      })
      .catch(() => {
        // Offline / users service down: the cached preference stands.
      });
    return () => controller.abort();
  }, [apiClient]);

  const prefs = useMemo(() => reconcilePrefs(stored, columns), [stored, columns]);

  const setPrefs = useCallback(
    (next: ColumnPrefs): void => {
      touchedRef.current = true;
      setStored(next);
      writeCachedPrefs(next);
      apiClient
        .saveLayout('user', ARCHIVE_COLUMNS_SCREEN_ID, {
          name: ARCHIVE_COLUMNS_LAYOUT_NAME,
          layout: toLayout(next) as unknown as Record<string, unknown>,
        })
        .then(() => setIsSavedLocallyOnly(false))
        .catch(() => setIsSavedLocallyOnly(true));
    },
    [apiClient]
  );

  return { prefs, setPrefs, isSavedLocallyOnly };
}
